import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaDumbbell, FaEye, FaEyeSlash, FaCheckCircle } from 'react-icons/fa';
import { MdError } from 'react-icons/md';
import toast from 'react-hot-toast';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';
import { useTenant } from '../../context/TenantContext';
import DateField from '../../components/common/DateField';
import { sanitizePhone, isValidPhone, sanitizeEmail, isValidEmail, PHONE_HINT, EMAIL_HINT } from '../../utils/inputRules';

const PasswordStrength = ({ password }) => {
  const checks = [
    { ok: password.length >= 8,       label: '8+ chars' },
    { ok: /[a-zA-Z]/.test(password),  label: 'Letter' },
    { ok: /[0-9]/.test(password),     label: 'Number' },
  ];
  const score = checks.filter(c => c.ok).length;
  const color = score === 3 ? 'bg-green-500' : score === 2 ? 'bg-yellow-400' : 'bg-red-400';
  return (
    <div className="mt-2 space-y-1">
      <div className="flex gap-1 h-1">
        {[0,1,2].map(i => <div key={i} className={`flex-1 rounded-full ${i < score ? color : 'bg-gray-200'}`} />)}
      </div>
      <div className="flex gap-3">
        {checks.map(c => <span key={c.label} className={`text-xs ${c.ok ? 'text-green-600' : 'text-gray-400'}`}>{c.ok ? '✓' : '○'} {c.label}</span>)}
      </div>
    </div>
  );
};

const ROLE_DASHBOARDS = { admin: '/admin/dashboard', trainer: '/trainer/dashboard', student: '/student/dashboard' };
const GENDERS = [
  { value: '', label: 'Prefer not to say' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];
const inputCls = 'w-full bg-[#1a1918] border border-[rgba(237,232,224,0.08)] rounded-xl px-4 py-3 text-sm text-[#ede8e0] placeholder-[#6b6560] focus:outline-none transition-colors';
const labelCls = 'block text-sm font-medium text-[#ede8e0] mb-1.5';
const groupCls = 'text-xs font-semibold text-[#8a847c] uppercase tracking-wide';
const todayISO = new Date().toISOString().split('T')[0];

export default function AcceptInvite() {
  const { token }    = useParams();
  const navigate     = useNavigate();
  const { login }    = useAuth();
  const { setTenantSlug } = useTenant();

  const [invitation, setInvitation] = useState(null);
  const [loadError,  setLoadError]  = useState('');
  const [loading,    setLoading]    = useState(true);

  // Account
  const [name,     setName]     = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  // Personal details
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [phone,   setPhone]   = useState('');
  const [gender,  setGender]  = useState('');
  const [street,  setStreet]  = useState('');
  const [city,    setCity]    = useState('');
  const [district, setDistrict] = useState('');
  // Student guardian / trainer specialization
  const [guardianName,  setGuardianName]  = useState('');
  const [guardianEmail, setGuardianEmail] = useState('');
  const [specialization, setSpecialization] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [formError,  setFormError]  = useState('');

  useEffect(() => {
    api.get(`/api/invitations/verify/${token}`)
      .then(res => setInvitation(res.data.invitation))
      .catch(err => setLoadError(err.response?.data?.message || 'Invalid invitation link'))
      .finally(() => setLoading(false));
  }, [token]);

  const role      = invitation?.role;
  const isStudent = role === 'student';
  const isTrainer = role === 'trainer';
  const needsDob  = isStudent || isTrainer;

  const phoneErr    = phone.trim() !== '' && !isValidPhone(phone);
  const guardianErr = guardianEmail.trim() !== '' && !isValidEmail(guardianEmail);

  const handleAccept = async () => {
    if (!name.trim()) { setFormError('Please enter your name'); return; }
    if (password.length < 8 || !/[a-zA-Z]/.test(password) || !/\d/.test(password)) {
      setFormError('Password must be at least 8 characters and include a letter and a number.');
      return;
    }
    if (needsDob && !dateOfBirth) { setFormError('Please enter your date of birth.'); return; }
    if (!street.trim() || !city.trim()) { setFormError('Please enter your address (street/tole and city).'); return; }
    if (phoneErr)    { setFormError('Enter a valid phone number.'); return; }
    if (guardianErr) { setFormError('Enter a valid guardian email.'); return; }

    setFormError('');
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(), password,
        dateOfBirth: dateOfBirth || undefined,
        phone: phone.trim() || undefined,
        gender: gender || undefined,
        address: (street || city || district) ? { street, city, district } : undefined,
      };
      if (isStudent) { payload.guardianName = guardianName.trim() || undefined; payload.guardianEmail = guardianEmail.trim() || undefined; }
      if (isTrainer) { payload.specialization = specialization.trim() || undefined; }

      const { data } = await api.post(`/api/invitations/${token}/accept`, payload);
      await setTenantSlug(data.user.clubSlug);
      login(data.user, data.token);
      toast.success(data.message || 'Welcome!');
      navigate(ROLE_DASHBOARDS[data.user.role] || '/login', { replace: true });
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to accept invitation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const accent = invitation?.club?.primaryColor || '#E24B4A';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#141312]">
        <div className="text-[#8a847c] text-sm">Validating invitation…</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#141312] p-6">
        <div className="max-w-sm w-full bg-[#1e1d1b] rounded-2xl border border-[rgba(237,232,224,0.08)] shadow p-8 text-center space-y-4">
          <div className="text-5xl">🔗</div>
          <h2 className="text-xl font-bold text-[#ede8e0]">Invitation Invalid</h2>
          <p className="text-[#8a847c] text-sm">{loadError}</p>
          <p className="text-xs text-[#8a847c]">Contact your club admin to get a new invitation.</p>
        </div>
      </div>
    );
  }

  const roleLabel = invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1);
  const focusOn  = (e) => (e.target.style.borderColor = accent);
  const focusOff = (e) => (e.target.style.borderColor = '');

  return (
    <div className="min-h-screen bg-[#141312] flex flex-col">
      {/* Header */}
      <div className="py-5 px-6 flex items-center justify-center border-b border-[rgba(237,232,224,0.08)] bg-[#1e1d1b]">
        <div className="flex items-center gap-2">
          <FaDumbbell className="text-xl" style={{ color: accent }} />
          <span className="font-bold text-[#ede8e0] text-lg">{invitation.club.name}</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md">
          <div className="bg-[#1e1d1b] rounded-2xl shadow-sm border border-[rgba(237,232,224,0.08)] p-5 sm:p-6 mb-4">
            {/* Invite banner */}
            <div className="flex items-start gap-3 mb-5 pb-5 border-b border-[rgba(237,232,224,0.08)]">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-lg font-bold flex-shrink-0"
                style={{ backgroundColor: accent }}>
                {invitation.club.name[0]}
              </div>
              <div>
                <h2 className="font-bold text-[#ede8e0] text-lg">You're invited!</h2>
                <p className="text-[#8a847c] text-sm">
                  <strong>{invitation.inviterName}</strong> invited you to join{' '}
                  <strong>{invitation.club.name}</strong> as a{' '}
                  <strong className="capitalize">{roleLabel}</strong>
                </p>
              </div>
            </div>

            {/* ── Account ── */}
            <p className={`${groupCls} mb-3`}>Account</p>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Your Full Name <span className="text-red-400">*</span></label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="Enter your name"
                  className={inputCls} onFocus={focusOn} onBlur={focusOff} />
              </div>
              <div>
                <label className={labelCls}>Email <span className="ml-2 text-xs text-[#8a847c] font-normal">(from invitation)</span></label>
                <input value={invitation.email} readOnly
                  className="w-full border border-[rgba(237,232,224,0.08)] rounded-xl px-4 py-3 text-sm bg-[#1a1918] text-[#8a847c] cursor-default" />
              </div>
              <div>
                <label className={labelCls}>Create Password <span className="text-red-400">*</span></label>
                <div className="relative">
                  <input value={password} onChange={e => setPassword(e.target.value)}
                    type={showPass ? 'text' : 'password'} placeholder="8+ chars, incl. a letter & number"
                    className={`${inputCls} pr-10`} onFocus={focusOn} onBlur={focusOff} />
                  <button type="button" onClick={() => setShowPass(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a847c]">
                    {showPass ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {password && <PasswordStrength password={password} />}
              </div>
            </div>

            {/* ── Personal details ── */}
            <p className={`${groupCls} mt-6 mb-3`}>Personal details</p>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>
                  Date of Birth {needsDob ? <span className="text-red-400">*</span> : <span className="text-xs text-[#8a847c] font-normal">(optional)</span>}
                </label>
                <DateField value={dateOfBirth} onChange={setDateOfBirth} label="Date of birth" placeholder={needsDob ? 'Select your date of birth' : 'Select date (optional)'} max={todayISO} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Phone <span className="text-xs text-[#8a847c] font-normal">(optional)</span></label>
                  <input value={phone} onChange={e => setPhone(sanitizePhone(e.target.value))} type="tel"
                    placeholder="98XXXXXXXX" className={inputCls}
                    style={phoneErr ? { borderColor: '#f87171' } : undefined}
                    onFocus={focusOn} onBlur={e => { if (!phoneErr) focusOff(e); }} />
                  {phoneErr && <p className="text-xs text-red-400 mt-1">{PHONE_HINT}</p>}
                </div>
                <div>
                  <label className={labelCls}>Gender <span className="text-xs text-[#8a847c] font-normal">(optional)</span></label>
                  <select value={gender} onChange={e => setGender(e.target.value)} className={inputCls} onFocus={focusOn} onBlur={focusOff}>
                    {GENDERS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls}>Address <span className="text-red-400">*</span></label>
                <input value={street} onChange={e => setStreet(e.target.value)} placeholder="Street / tole *"
                  className={`${inputCls} mb-2`} onFocus={focusOn} onBlur={focusOff} />
                <div className="grid grid-cols-2 gap-3">
                  <input value={city} onChange={e => setCity(e.target.value)} placeholder="City *" className={inputCls} onFocus={focusOn} onBlur={focusOff} />
                  <input value={district} onChange={e => setDistrict(e.target.value)} placeholder="District (optional)" className={inputCls} onFocus={focusOn} onBlur={focusOff} />
                </div>
              </div>
            </div>

            {/* ── Guardian (student) ── */}
            {isStudent && (
              <>
                <p className={`${groupCls} mt-6 mb-3`}>Parent / Guardian <span className="normal-case font-normal text-[#8a847c]">(optional)</span></p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Guardian Name</label>
                    <input value={guardianName} onChange={e => setGuardianName(e.target.value)} placeholder="Parent / guardian"
                      className={inputCls} onFocus={focusOn} onBlur={focusOff} />
                  </div>
                  <div>
                    <label className={labelCls}>Guardian Email</label>
                    <input value={guardianEmail} onChange={e => setGuardianEmail(sanitizeEmail(e.target.value))} type="email"
                      placeholder="guardian@email.com" className={inputCls}
                      style={guardianErr ? { borderColor: '#f87171' } : undefined}
                      onFocus={focusOn} onBlur={e => { if (!guardianErr) focusOff(e); }} />
                    {guardianErr && <p className="text-xs text-red-400 mt-1">{EMAIL_HINT}</p>}
                  </div>
                </div>
              </>
            )}

            {/* ── Specialization (trainer) ── */}
            {isTrainer && (
              <>
                <p className={`${groupCls} mt-6 mb-3`}>Coaching</p>
                <div>
                  <label className={labelCls}>Specialization <span className="text-xs text-[#8a847c] font-normal">(optional)</span></label>
                  <input value={specialization} onChange={e => setSpecialization(e.target.value)} placeholder="e.g. Taekwondo, Sparring"
                    className={inputCls} onFocus={focusOn} onBlur={focusOff} />
                </div>
              </>
            )}

            {formError && (
              <div className="flex items-start gap-2 bg-red-400/10 text-red-400 rounded-xl p-3 text-sm mt-5">
                <MdError className="text-lg flex-shrink-0 mt-0.5" />
                <span>{formError}</span>
              </div>
            )}

            <button onClick={handleAccept} disabled={submitting}
              className="w-full mt-5 py-3 rounded-full font-medium text-white bg-primary hover:bg-primary-dark transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ backgroundColor: accent }}>
              {submitting ? 'Creating account…' : (<><FaCheckCircle /> Join {invitation.club.name}</>)}
            </button>
          </div>

          <p className="text-center text-xs text-[#8a847c]">
            Already have an account?{' '}
            <button onClick={() => { localStorage.setItem('trainaxis_club_slug', invitation.club.slug); navigate('/login'); }}
              className="font-semibold hover:underline" style={{ color: accent }}>
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
